import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { BookIcon, SearchIcon, ArrowRightIcon, XIcon, StarIcon, GraduationCapIcon, BookmarkIcon } from 'lucide-react';
import { Sitemap } from '../components/Sitemap';
import { Link } from 'react-router-dom';
import { SkeletonCard } from '../components/ui/SkeletonLoader';
import { SearchGrammar } from '../components/SearchGrammar';
import { GrammarFilters } from '../components/GrammarFilters';
const GRAMMAR_TOPICS = [{
  id: 'present-tense',
  title: {
    en: 'Present Tense',
    ar: 'زمن المضارع'
  },
  description: {
    en: 'Learn how to talk about habits and current actions',
    ar: 'تعلم كيفية التحدث عن العادات والأفعال الحالية'
  },
  level: 'beginner',
  examples: [{
    en: 'I study English every day',
    ar: 'أدرس الإنجليزية كل يوم'
  }, {
    en: 'She works in London',
    ar: 'هي تعمل في لندن'
  }]
}, {
  id: 'past-tense',
  title: {
    en: 'Past Tense',
    ar: 'زمن الماضي'
  },
  description: {
    en: 'Express completed actions and past experiences',
    ar: 'التعبير عن الأفعال المكتملة والتجارب الماضية'
  },
  examples: [{
    en: 'I visited Paris last year',
    ar: 'زرت باريس العام الماضي'
  }, {
    en: 'They studied together',
    ar: 'درسوا معاً'
  }]
}, {
  id: 'future-tense',
  title: {
    en: 'Future Tense',
    ar: 'زمن المستقبل'
  },
  description: {
    en: 'Discuss plans and make predictions',
    ar: 'مناقشة الخطط وتقديم التنبؤات'
  },
  examples: [{
    en: 'I will travel next summer',
    ar: 'سأسافر الصيف القادم'
  }, {
    en: 'She is going to study medicine',
    ar: 'ستدرس الطب'
  }]
}];
export function GrammarGuide() {
  const {
    isArabic
  } = useLanguage();
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [bookmarkedTopics, setBookmarkedTopics] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({
    difficulty: [] as string[],
    category: [] as string[]
  });
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
  const handleFilterChange = (category: 'difficulty' | 'category', value: string) => {
    setFilters(prev => {
      const current = prev[category];
      const updated = current.includes(value) ? current.filter(item => item !== value) : [...current, value];
      return {
        ...prev,
        [category]: updated
      };
    });
  };
  const clearFilters = () => {
    setFilters({
      difficulty: [],
      category: []
    });
  };
  const filteredTopics = useMemo(() => {
    return GRAMMAR_TOPICS.filter(topic => {
      const searchMatch = search.trim() === '' || topic.title.en.toLowerCase().includes(search.toLowerCase()) || topic.title.ar.includes(search) || topic.description.en.toLowerCase().includes(search.toLowerCase()) || topic.description.ar.includes(search);
      const difficultyMatch = filters.difficulty.length === 0 || filters.difficulty.includes(topic.level || 'beginner');
      const categoryMatch = filters.category.length === 0 || filters.category.some(cat => topic.categories?.includes(cat));
      return searchMatch && difficultyMatch && categoryMatch;
    });
  }, [search, filters]);
  const toggleBookmark = (topicId: string) => {
    setBookmarkedTopics(prev => {
      const next = new Set(prev);
      if (next.has(topicId)) {
        next.delete(topicId);
      } else {
        next.add(topicId);
      }
      return next;
    });
  };
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-2872">
      <Sitemap currentPage={{
      en: 'Grammar Guide',
      ar: 'دليل القواعد'
    }} breadcrumbs={[{
      label: {
        en: 'Resources',
        ar: 'المصادر'
      },
      href: '/resources'
    }]} data-id="element-2873" />
      <div className="max-w-7xl mx-auto" data-id="element-2874">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" data-id="element-2875">
          <div className="lg:col-span-1" data-id="element-2876">
            <GrammarFilters selectedFilters={filters} onFilterChange={handleFilterChange} onClearFilters={clearFilters} data-id="element-2877" />
          </div>
          <div className="lg:col-span-3" data-id="element-2878">
            <SearchGrammar value={search} onChange={setSearch} onClear={() => setSearch('')} resultsCount={filteredTopics.length} className="mb-8" data-id="element-2879" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2880">
              {isLoading ? Array.from({
              length: 4
            }).map((_, index) => <SkeletonCard key={index} data-id="element-2881" />) : filteredTopics.map((topic, index) => <div key={index} className="group bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 p-6" data-id="element-2882">
                      <div className="flex items-start justify-between mb-4" data-id="element-2883">
                        <div className="flex items-center gap-3" data-id="element-2884">
                          <BookIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-2885" />
                          <h2 className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-2886">
                            {isArabic ? topic.title.ar : topic.title.en}
                          </h2>
                        </div>
                        <button onClick={() => toggleBookmark(topic.id)} className={`p-2 rounded-lg transition-colors ${bookmarkedTopics.has(topic.id) ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'}`} data-id="element-2887">
                          <BookmarkIcon className="w-5 h-5" data-id="element-2888" />
                        </button>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 mb-6" data-id="element-2889">
                        {isArabic ? topic.description.ar : topic.description.en}
                      </p>
                      <div className="space-y-4 mb-6" data-id="element-2890">
                        {topic.examples.map((example, i) => <div key={i} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4" data-id="element-2891">
                            <div className="flex items-center justify-between" data-id="element-2892">
                              <span className="text-gray-900 dark:text-white" data-id="element-2893">
                                {example.en}
                              </span>
                              <span className="text-blue-600 dark:text-blue-400" dir="rtl" data-id="element-2894">
                                {example.ar}
                              </span>
                            </div>
                          </div>)}
                      </div>
                      <Link to={`/resources/grammar/${topic.id}`} className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium group" data-id="element-2895">
                        <span data-id="element-2896">{isArabic ? 'تعلم المزيد' : 'Learn More'}</span>
                        <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-2897" />
                      </Link>
                    </div>)}
            </div>
            {!isLoading && filteredTopics.length === 0 && <div className="text-center py-12" data-id="element-2898">
                <p className="text-gray-500 dark:text-gray-400" data-id="element-2899">
                  {isArabic ? 'لا توجد نتائج تطابق بحثك' : 'No topics match your search'}
                </p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}